export enum TransactionType {
  BOOKING = 'BOOKING',
  ADDON = 'ADDON',
  SUBSCRIPTION = 'SUBSCRIPTION',
  CLUB_RESOURCE = 'CLUB_RESOURCE',
  PORT_WATCH_BUYOUT = 'PORT_WATCH_BUYOUT',
  SERVICE_BOOKING = 'SERVICE_BOOKING',
}

export enum TransactionStatus {
  SUCCEEDED = 'SUCCEEDED',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED',
  PENDING = 'PENDING',
}

export interface User {
  id: string;
  name: string | null;
  email: string;
  billing_address_line_1?: string;
  city?: string;
  post_code?: string;
}

export type NetvisorPostingStatus = 'POSTED' | 'FAILED' | 'PENDING' | 'NOT_REQUIRED';

export interface NetvisorPostingStatusFields {
  netvisor_posting_eligible?: boolean;
  netvisor_posting_status?: NetvisorPostingStatus | null;
  netvisor_invoice_id?: string | null;
  netvisor_exported_at?: string | null;
  netvisor_export_error?: string | null;
}

export interface TransactionItem {
  id: string;
  type: string; // e.g., SUBSCRIPTION | CLUB_RESOURCE | BOOKING | ADDON
  amount: number;
  status: string; // e.g., SUCCEEDED | FAILED | REFUNDED
  payment_intent_id?: string | null;
  paytrail_payment_id?: string | null;
  stripe_refund_id?: string | null;
  refunded_amount: number;
  currency: string; // e.g., EUR
  created_at: string; // ISO datetime
  user_id?: string | null;
  user?: User | null;
}

export interface SubscriptionOwnerData {
  id: string;
  name: string;
  name_sub_title: string;
  price: number;
  billing_cycle: number;
  subscription_type: number;
  status: number;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionOwner {
  status: true;
  message: string;
  data: SubscriptionOwnerData;
}

export interface ClubResourceItem {
  id: string;
  resource: {
    id: string;
    name: string;
    resource_type: number;
    quantity: number | null;
    slot_length: string | null;
    price: number;
    price_unit: string;
  };
  start_time: string;
  end_time: string;
  quantity: number | null;
  total_price: number;
  status: number;
  special_requests: string | null;
  cancellation_reason: string | null;
  created_at: string;
  net_amount?: number | null;
  vat_amount?: number | null;
  gross_amount?: number | null;
  vat_rate?: number | null;
  tax_regime?: string | null;
  customer_country?: string | null;
}

export interface ClubResourceOwner {
  id: string;
  club?: {
    id: string;
    name: string;
    logo: string | null;
  };
  items: ClubResourceItem[];
  total_price: number;
  status: number;
  created_at: string;
  payment_provider?: string | null;
  net_amount?: number | string | null;
  vat_amount?: number | string | null;
  gross_amount?: number | string | null;
  customer_country?: string | null;
  customer_vat_id?: string | null;
  items_snapshot?: ItemsSnapshot | null;
}

export interface BookingAddonItem {
  id: string;
  quantity: number;
  price_at_booking: number;
  price_unit_at_booking: string;
  no_of_slots: number;
  start_date: string;
  end_date: string;
  gross_amount?: number;
  vat_rate?: number;
  addon?: {
    id: string;
    name: string;
  };
}

export interface BookingOwnerData {
  id: string;
  berth: Record<string, unknown> | null;
  boat_details: Record<string, unknown> | null;
  start_date: string;
  end_date: string;
  total_price: number;
  payment_type: number;
  status: number;
  special_requests: string | null;
  cancellation_reason: string | null;
  booking_addons: BookingAddonItem[];
  is_modified: boolean;
  created_at: string;
  payment_provider?: string | null;
  harbor?: {
    name?: string | null;
    default_check_in_time?: string | null;
    default_check_out_time?: string | null;
    timezone?: string | null;
  } | null;
}

export interface BookingOwner {
  status: true;
  message: string;
  data: BookingOwnerData;
}

export interface BaseTransactionData extends NetvisorPostingStatusFields {
  id: string;
  type: TransactionType;
  amount: number;
  status: string;
  payment_intent_id: string | null;
  paytrail_payment_id: string | null;
  stripe_refund_id: string | null;
  refunded_amount: number;
  psp_fee?: string | number | null;
  currency: string;
  created_at: string;
  user_id: string | null;
  user: User | null;
  payment_provider?: string | null;
  items_snapshot?: ItemsSnapshot | null;
  discount_applied?: boolean;
  discount_code?: string | null;
  discount_label?: string | null;
  discount_description?: string | null;
  discount_type?: string | null;
  discount_value?: number | null;
  discount_amount?: number | null;
  original_amount?: number | null;
  final_amount?: number | null;
  original_gross_amount?: number | null;
  final_gross_amount?: number | null;
  net_amount?: number | string | null;
  vat_amount?: number | string | null;
  gross_amount?: number | string | null;
  vat_rate?: number | string | null;
  tax_regime?: string | null;
  customer_country?: string | null;
  product_type?: string | null;
}

export interface BaseTransactionResponse {
  status: boolean;
  message: string;
  data: BaseTransactionData;
}

export interface SubscriptionTransaction extends BaseTransactionResponse {
  data: BaseTransactionData & { type: TransactionType.SUBSCRIPTION };
  owner: SubscriptionOwner;
}

export interface ClubResourceTransaction extends BaseTransactionResponse {
  data: BaseTransactionData & { type: TransactionType.CLUB_RESOURCE };
  owner: ClubResourceOwner;
}

export interface PortWatchBuyoutOwner {
  id: string;
  status: string;
  duty_units: number;
  amount: number;
  currency: string;
  obligation_period_start: string;
  obligation_period_end: string;
  completed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  club: {
    id: string;
    name: string;
    logo: string | null;
    country_short_code?: string | null;
  } | null;
  resource: {
    id: string;
    name: string;
    resource_type: number;
    port_watch_buyout_amount?: number | string | null;
  } | null;
  shift: {
    id: string;
    shift_start: string;
    shift_end: string;
    status: number | string;
    assigned_member_id?: string | null;
    assigned_at?: string | null;
    completed_at?: string | null;
    club?: {
      id: string;
      name: string;
      logo: string | null;
    } | null;
    resource?: {
      id: string;
      name: string;
      resource_type: number;
    } | null;
  } | null;
  user: User | null;
  items_snapshot?: ItemsSnapshot | null;
  net_amount?: number | string | null;
  vat_amount?: number | string | null;
  gross_amount?: number | string | null;
  vat_rate?: number | string | null;
  tax_regime?: string | null;
  customer_country?: string | null;
  product_type?: string | null;
}

export interface PortWatchBuyoutTransaction extends BaseTransactionResponse {
  data: BaseTransactionData & { type: TransactionType.PORT_WATCH_BUYOUT };
  owner: PortWatchBuyoutOwner;
}

export interface BookingTransaction extends BaseTransactionResponse {
  data: BaseTransactionData & {
    type: TransactionType.BOOKING | TransactionType.ADDON;
  };
  owner: BookingOwner;
}

export interface ServiceBookingOwnerService {
  id: string;
  name: string;
  description: string | null;
  price_amount: string; // e.g. "25.00"
  owner_id: string;
  booking_slot_type: string; // e.g. "daily"
}

export interface ServiceBookingOwner {
  id: string;
  service: ServiceBookingOwnerService;
  service_provider: unknown | null;
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
  start_time: string; // HH:mm
  end_time: string; // HH:mm
  total_price: number;
  quantity: number;
  status: number;
  special_requests: string | null;
  cancellation_reason: string | null;
  payment_transactions: unknown[];
  created_at: string;
  payment_provider: string | null;
}

export interface ServiceBookingTransaction extends BaseTransactionResponse {
  data: BaseTransactionData & { type: TransactionType.SERVICE_BOOKING };
  owner: ServiceBookingOwner;
}

export interface ItemsSnapshotTax {
  tax_regime: string;
  customer_vat_id: string | null;
  customer_country: string | null;
  product_vat_config_id: string;
  customer_vat_id_validation: string;
}

export interface ItemsSnapshotItem {
  tax: ItemsSnapshotTax;
  line_id?: string;
  vat_rate?: number;
  net_amount?: number;
  vat_amount?: number;
  description?: string;
  gross_amount?: number;
  product_type?: string;
}

export interface ItemsSnapshotTotals {
  net_amount: number;
  vat_amount: number;
  gross_amount: number;
}

export interface ItemsSnapshotDiscount {
  code?: string;
  label?: string;
  description?: string;
  discount_type?: string;
  discount_value?: number;
  discount_amount?: number;
  original_amount?: number;
  final_amount?: number;
  original_net_amount?: number;
  final_net_amount?: number;
  original_gross_amount?: number;
  final_gross_amount?: number;
}

export interface ItemsSnapshot {
  items: ItemsSnapshotItem[];
  totals: ItemsSnapshotTotals;
  discount?: ItemsSnapshotDiscount | null;
  buyout?: {
    kind?: string;
    buyout_id?: string;
    club_id?: string;
    resource_id?: string;
    duty_units?: number;
  };
}

export type TransactionResponse =
  | SubscriptionTransaction
  | ClubResourceTransaction
  | PortWatchBuyoutTransaction
  | BookingTransaction
  | ServiceBookingTransaction;

export function isSubscriptionTx(
  tx: TransactionResponse
): tx is SubscriptionTransaction {
  return tx.data.type === TransactionType.SUBSCRIPTION;
}

export function isClubResourceTx(
  tx: TransactionResponse
): tx is ClubResourceTransaction {
  return tx.data.type === TransactionType.CLUB_RESOURCE;
}

export function isPortWatchBuyoutTx(
  tx: TransactionResponse
): tx is PortWatchBuyoutTransaction {
  return tx.data.type === TransactionType.PORT_WATCH_BUYOUT;
}

export function isBookingTx(tx: TransactionResponse): tx is BookingTransaction {
  return (
    tx.data.type === TransactionType.BOOKING ||
    tx.data.type === TransactionType.ADDON
  );
}

export function isServiceBookingTx(
  tx: TransactionResponse
): tx is ServiceBookingTransaction {
  return tx.data.type === TransactionType.SERVICE_BOOKING;
}

export interface TransactionsListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  type?: string;
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
}

export interface TransactionsListApiRaw {
  status: boolean;
  message?: string;
  total_items?: number;
  current_page?: number;
  total_pages?: number;
  data: TransactionItem[];
}

export interface TransactionsListNormalized {
  items: TransactionItem[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  pageLimit: number;
}

export interface TransactionsSummaryByType {
  type: string;
  count: number;
}

export interface TransactionsSummary {
  totalTransactions: number;
  totalSuccessfulAmount: number;
  failedTransactions: number;
  transactionsByType: TransactionsSummaryByType[];
}

export interface TransactionsSummaryApiRaw {
  status: boolean;
  message?: string;
  data: {
    totalTransactions: number;
    totalSuccessfulAmount: number;
    failedTransactions: number;
    transactionsByType: TransactionsSummaryByType[];
  };
}
